import { Eye, Plus, X, Trash, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  ItemProject,
  NewItemForm,
  NewProjectForm,
  Project,
  ProjectWithItems,
} from "../../firebase/db.type";
import { FirebaseService } from "../../firebase/firebase.service";

const objectCloudinary = {
  cloudName: "hoangdev",
  apiKey: "524287457883745",
  apiSecret: "OO_2IGOgm4jeK-bolGcbha6ZdoU",
}

// Component chính với TypeScript
const ProjectManagementApp: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<ProjectWithItems | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showAddProject, setShowAddProject] = useState<boolean>(false);
  const [showAddItem, setShowAddItem] = useState<boolean>(false);
  const [uploadingImage, setUploadingImage] = useState<boolean>(false);

  // Form states với type safety
  const [newProject, setNewProject] = useState<NewProjectForm>({
    name: "",
    imageUrl: "",
  });
  const [newItem, setNewItem] = useState<NewItemForm>({
    imageUrl: "",
  });

  // File states for image upload
  const [selectedProjectImage, setSelectedProjectImage] = useState<File | null>(null);
  const [selectedItemImage, setSelectedItemImage] = useState<File | null>(null);

  // Load projects khi component mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Function to upload image to Cloudinary
  const uploadImageToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'vrxbopzg'); // You may need to create this preset in your Cloudinary dashboard
    formData.append('cloud_name', objectCloudinary.cloudName);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${objectCloudinary.cloudName}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      return data.secure_url; // Return the secure URL of the uploaded image
    } catch (error) {
      console.error('Error uploading image to Cloudinary:', error);
      throw error;
    }
  };

  const loadProjects = async (): Promise<void> => {
    setLoading(true);
    try {
      const projectList = await FirebaseService.getProjects();
      setProjects(projectList);
    } catch (error) {
      console.error("Failed to load projects:", error);
    } finally {
      setLoading(false);
    }
  };

  // Thêm project mới
  const handleAddProject = async (): Promise<void> => {
    if (!newProject.name.trim()) return;

    setUploadingImage(true);
    try {
      let imageUrl = newProject.imageUrl;
      
      // If there's a selected file, upload it to Cloudinary
      if (selectedProjectImage) {
        imageUrl = await uploadImageToCloudinary(selectedProjectImage);
      }

      await FirebaseService.addProject({
        name: newProject.name,
        imageUrl: imageUrl,
      });
      
      setNewProject({ name: "", imageUrl: "" });
      setSelectedProjectImage(null);
      setShowAddProject(false);
      await loadProjects(); // Reload danh sách
    } catch (error) {
      console.error("Failed to add project:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  // Thêm item project
  const handleAddItem = async (): Promise<void> => {
    if (!selectedProject) return;

    setUploadingImage(true);
    try {
      let imageUrl = newItem.imageUrl;
      
      // If there's a selected file, upload it to Cloudinary
      if (selectedItemImage) {
        imageUrl = await uploadImageToCloudinary(selectedItemImage);
      }

      if (!imageUrl.trim()) {
        throw new Error('Image URL is required');
      }

      await FirebaseService.addItemProject({
        projectId: selectedProject.id,
        imageUrl: imageUrl,
      });
      
      setNewItem({ imageUrl: "" });
      setSelectedItemImage(null);
      setShowAddItem(false);
      // Reload chi tiết project
      const updatedProject = await FirebaseService.getProjectWithItems(
        selectedProject.id
      );
      if (updatedProject) {
        setSelectedProject(updatedProject);
      }
    } catch (error) {
      console.error("Failed to add item:", error);
    } finally {
      setUploadingImage(false);
    }
  };

  // Xem chi tiết project
  const handleViewProject = async (project: Project): Promise<void> => {
    setLoading(true);
    try {
      const projectWithItems = await FirebaseService.getProjectWithItems(
        project.id
      );
      if (projectWithItems) {
        setSelectedProject(projectWithItems);
      }
    } catch (error) {
      console.error("Failed to load project details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (project: Project): Promise<void> => {
    setLoading(true);
    try {
      const projectWithItems = await FirebaseService.deleteProject(project.id);
      setProjects(projectWithItems);
    } catch (error) {
      console.error("Failed to load project details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Event handlers với type safety
  const handleProjectNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewProject({ ...newProject, name: e.target.value });
  };

  const handleProjectImageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewProject({ ...newProject, imageUrl: e.target.value });
  };

  const handleItemImageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setNewItem({ ...newItem, imageUrl: e.target.value });
  };

  // File input handlers
  const handleProjectFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedProjectImage(file);
      setNewProject({ ...newProject, imageUrl: "" }); // Clear URL input when file is selected
    }
  };

  const handleItemFileChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedItemImage(file);
      setNewItem({ ...newItem, imageUrl: "" }); // Clear URL input when file is selected
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Management Projects</h1>

        {!selectedProject ? (
          // Danh sách projects
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                List Projects
              </h2>
              <button
                onClick={() => setShowAddProject(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                <span>Add Project</span>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project: Project) => (
                  <div
                    key={project.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden"
                  >
                    <img
                      src={
                        project.imageUrl ||
                        "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=250&fit=crop"
                      }
                      alt={project.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {project.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewProject(project)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center gap-2"
                        >
                          <Eye size={16} />
                          View details
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project)}
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded flex items-center gap-2"
                        >
                          <Trash size={16} />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Chi tiết project
          <div>
            <div className="flex justify-between items-center mb-6">
              <div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-blue-600 hover:text-blue-800 mb-2"
                >
                  ← Back
                </button>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedProject.name}
                </h2>
              </div>
              <button
                onClick={() => setShowAddItem(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Plus size={20} />
                Add Item
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <img
                src={
                  selectedProject.imageUrl ||
                  "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=300&fit=crop"
                }
                alt={selectedProject.name}
                className="w-full h-64 object-cover"
              />
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Items of project ({selectedProject.items?.length || 0})
              </h3>

              {loading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {selectedProject.items?.map((item: ItemProject) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-lg shadow-md overflow-hidden"
                    >
                      <img
                        src={item.imageUrl}
                        alt="Project Item"
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-2">
                        <p className="text-sm text-gray-600">
                          Item ID: {item.id}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal thêm project */}
        {showAddProject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add new project</h3>
                <button onClick={() => setShowAddProject(false)}>
                  <X size={20} />
                </button>
              </div>
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name project *
                  </label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={handleProjectNameChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload image from computer
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProjectFileChange}
                      className="hidden"
                      id="project-image-upload"
                    />
                    <label
                      htmlFor="project-image-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload size={24} className="text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {selectedProjectImage ? selectedProjectImage.name : "Click to select image"}
                      </span>
                    </label>
                  </div>
                  {selectedProjectImage && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {selectedProjectImage.name}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or enter image URL
                  </label>
                  <input
                    type="url"
                    value={newProject.imageUrl}
                    onChange={handleProjectImageChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="https://example.com/image.jpg"
                    disabled={!!selectedProjectImage}
                  />
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddProject(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    disabled={uploadingImage}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddProject}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                    disabled={uploadingImage || (!newProject.name.trim() || (!selectedProjectImage && !newProject.imageUrl.trim()))}
                  >
                    {uploadingImage ? (
                      <div className="flex items-center gap-2">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Uploading...
                      </div>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal thêm item */}
        {showAddItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Add new item to project</h3>
                <button onClick={() => setShowAddItem(false)}>
                  <X size={20} />
                </button>
              </div>
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload image from computer *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleItemFileChange}
                      className="hidden"
                      id="item-image-upload"
                    />
                    <label
                      htmlFor="item-image-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload size={24} className="text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {selectedItemImage ? selectedItemImage.name : "Click to select image"}
                      </span>
                    </label>
                  </div>
                  {selectedItemImage && (
                    <p className="text-xs text-green-600 mt-1">
                      Selected: {selectedItemImage.name}
                    </p>
                  )}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Or enter image URL
                  </label>
                  <input
                    type="url"
                    value={newItem.imageUrl}
                    onChange={handleItemImageChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="https://example.com/image.jpg"
                    disabled={!!selectedItemImage}
                  />
                </div>
                
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddItem(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    disabled={uploadingImage}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                    disabled={uploadingImage || (!selectedItemImage && !newItem.imageUrl.trim())}
                  >
                    {uploadingImage ? (
                      <div className="flex items-center gap-2">
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Uploading...
                      </div>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectManagementApp;
